"use client";

import Link from "next/link";
import { useEffect, useState, useSyncExternalStore } from "react";
import type { Image } from "@/lib/shopify/types";
import { cn } from "@/lib/format";
import { ProductImage } from "@/components/ui/ProductImage";
import { PauseIcon, PlayIcon } from "@/components/ui/icons";

const ROTATE_MS = 6000;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const subscribeToMotion = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};

// Boxes are photographed to fill the frame; single products are shot on white,
// so they're shown whole on the linen tile instead of cropped.
export type HeroPhoto = { image: Image; title: string; href: string; fit: "cover" | "contain" };

// The photos beside the home page pitch, crossfading one into the next, each
// labelled with the box or product it shows. The photo on show drifts back to
// its resting size, and the bar under it fills until the next one. Reduced
// motion shows the first photo only.
export function HeroPhotos({ photos }: { photos: HeroPhoto[] }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // The server can't know the setting, so it renders the still version.
  const still = useSyncExternalStore(subscribeToMotion, () => window.matchMedia(REDUCED_MOTION).matches, () => true);
  const rotating = photos.length > 1 && !still;

  useEffect(() => {
    if (paused || !rotating) return;
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible") setActive((index) => (index + 1) % photos.length);
    }, ROTATE_MS);
    return () => window.clearTimeout(timer);
  }, [active, paused, rotating, photos.length]);

  const current = photos[active];

  return (
    <>
      {photos.map((photo, index) => (
        <div
          key={photo.href}
          className={cn(
            "absolute inset-0 -z-10 bg-tissue transition-opacity duration-[1200ms] ease-in-out",
            index === active ? "opacity-100" : "opacity-0",
          )}
          aria-hidden={index !== active}
        >
          <ProductImage
            image={photo.image}
            alt={photo.title}
            sizes="(min-width: 768px) 55vw, 100vw"
            preload={index === 0}
            fit={photo.fit}
            className={cn(
              "size-full",
              // Restarts each time this photo comes round again.
              index === active && rotating && "[&_img]:animate-[settle_7s_var(--ease-soft)_both]",
            )}
          />
        </div>
      ))}
      <div className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-4">
        <Link
          key={current.href}
          href={current.href}
          className="rise min-w-0 truncate rounded-[4px] bg-white/90 px-3 py-1.5 text-sm text-ink backdrop-blur-sm transition-colors hover:bg-white"
        >
          {current.title}
        </Link>
        {rotating ? (
          <div className="flex shrink-0 items-center gap-3 rounded-[4px] bg-white/90 px-3 py-2 text-ink backdrop-blur-sm">
            <ol className="flex items-center gap-1.5">
              {photos.map((photo, index) => (
                <li key={photo.href}>
                  <button
                    type="button"
                    onClick={() => setActive(index)}
                    className="flex h-5 items-center"
                    aria-label={`Show ${photo.title}`}
                    aria-current={index === active}
                  >
                    <span className="block h-[2px] w-6 overflow-hidden rounded-full bg-ink/20">
                      {index === active ? (
                        <span
                          key={`${active}-${paused}`}
                          className="block h-full origin-left bg-ink"
                          style={{ animation: paused ? "none" : `fill-x ${ROTATE_MS}ms linear both` }}
                        />
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => setPaused(!paused)}
              className="flex size-5 items-center justify-center"
              aria-label={paused ? "Play slideshow" : "Pause slideshow"}
            >
              {paused ? <PlayIcon /> : <PauseIcon />}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
